import _ from "lodash";
import { connect } from "react-redux";
import { Form } from "formik";
import jwt_decode from "jwt-decode";
import React, { useState } from "react";
import { withRouter } from "react-router-dom";

import { authRequests } from "@/api/(requests)/auth";
import Button from "@/components/UI/Button";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import OneTimeCodeInput, { transformErrors } from "@/components/Common/OneTimeCodeInput";
import { showAlertWithAction } from "@/redux/actions/alerts";
import { workflowRequests } from "@/api/(requests)/workflow";
import { workflowTokenStore } from "@/api/helpers/workflowTokenStore";

import "./EnterOTC.styles.scss";

const EnterOTC = (props) => {
  const { setSharedWorkflowData, sharedWorkflowData } = props;

  const isExistingUser = sharedWorkflowData.workflow_instance.state.username;

  const [otc, setOtc] = useState("");
  const [errors, setErrors] = useState([]);

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      const data = {
        otc: otc,
      };
      const otcVerificationResponse = await authRequests.verify_otc({ data });

      if (otcVerificationResponse.errors) {
        const transformedErrors = transformErrors(otcVerificationResponse.errors);

        setErrors(transformedErrors);

        const state = {};

        otcVerificationResponse.errors.forEach((error) => {
          if (error === "INVALID_OTC") {
            state["invalid_otc_used"] = true;
          }

          if (error === "ATTEMPT_AFTER_EXPIRED") {
            state["expired_otc_used"] = true;
          }
        });

        if (_.isEmpty(state)) {
          return;
        }

        const data = {
          node_name: "OTC",
          state: state,
          workflow_instance_entity_id: sharedWorkflowData.workflow_instance.entity_id,
          workflow_instance_sort_key: sharedWorkflowData.workflow_instance.sort_key,
        };
        const response = await workflowRequests.update_state_and_get_next_node({
          data,
        });

        setSharedWorkflowData({
          ...sharedWorkflowData,
          workflow_instance: response.workflow_instance,
        });
      } else {
        let idToken = otcVerificationResponse.auth.AuthenticationResult.IdToken;
        const username = jwt_decode(idToken)["cognito:username"];
        const data = {
          node_name: "OTC",
          state: {
            otc_auth_success: true,
            username: username,
          },
          workflow_instance_entity_id: sharedWorkflowData.workflow_instance.entity_id,
          workflow_instance_sort_key: sharedWorkflowData.workflow_instance.sort_key,
        };

        workflowTokenStore.set({
          idToken,
          refreshToken: otcVerificationResponse.auth.AuthenticationResult.RefreshToken,
        });

        const response = await workflowRequests.update_state_and_get_next_node({
          data,
        });

        setSharedWorkflowData({
          ...sharedWorkflowData,
          workflow_instance: response.workflow_instance,
        });
      }
    } catch (e) {
      console.log(e);
      props.showAlertWithAction("Error verifying one-time code.");
    }
  };

  const signInWithCredentialsClickHandler = async () => {
    try {
      const data = {
        node_name: "OTC",
        state: {
          otc_auth_success: false,
          request_to_sign_in_with_credentials: true,
        },
        workflow_instance_entity_id: sharedWorkflowData.workflow_instance.entity_id,
        workflow_instance_sort_key: sharedWorkflowData.workflow_instance.sort_key,
      };
      const response = await workflowRequests.update_state_and_get_next_node({ data });

      setSharedWorkflowData({
        ...sharedWorkflowData,
        workflow_instance: response.workflow_instance,
      });
    } catch (error) {
      captureErrorMessage(parseError({ error, fallback: "Failed to sign in with credentials" }), {
        tags: { component: "EnterOTC", errorType: "sign_in_with_credentials" },
      });
      props.showAlertWithAction("Error signing in with credentials.");
    }
  };

  const renderForm = () => {
    return (
      <Form style={{ marginBottom: 200 }}>
        <div className="workflow-container-sm">
          <div className="mx-auto px-0 my-5">
            <OneTimeCodeInput
              codeLength={5}
              errors={errors}
              setErrors={setErrors}
              setOtc={setOtc}
            />
          </div>
        </div>

        {!isExistingUser && (
          <div className="workflow-container-sm">
            <div className="mx-auto px-0 mb-1">
              <div className="d-flex justify-content-between mb-5">
                <Button
                  className="Btn Btn--otl-gry Btn--sm-2 workflow-btn"
                  onClick={props.backHandler}
                  type="button"
                >
                  Back
                </Button>
                <Button
                  className="Btn Btn--pri Btn--sm-2 workflow-btn"
                  disabled={otc.length < 5 ? "disabled" : null}
                  onClick={submitHandler}
                  type="submit"
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        )}

        {isExistingUser && (
          <div className="d-flex justify-content-between flex-column align-items-center">
            <Button
              className="Btn Btn--pri Btn--sm-2 workflow-btn-lg"
              disabled={otc.length < 5 ? "disabled" : null}
              key="submit"
              onClick={submitHandler}
              type="submit"
            >
              Sign in
            </Button>
            <div
              className="d-flex align-items-center pt-4 pb-4 tw:justify-center"
              style={{ color: "#6C757D" }}
            >
              <hr className="lines mr-3" />
              or
              <hr className="lines ml-3" />
            </div>
            <Button
              className="Btn Btn--otl-pri Btn--sm-2 workflow-btn-lg"
              onClick={signInWithCredentialsClickHandler}
              type="button"
            >
              Sign in with credentials
            </Button>
          </div>
        )}
      </Form>
    );
  };

  return (
    <>
      <div className="p-lg-5 p-3 my-5">
        {!isExistingUser && (
          <>
            <div className="fs-22 txt-pri mb-5 text-center tw:font-bold">
              Enter one-time code to continue
            </div>
            <div className="text-center mt-3 mb-4 workflow-container-sm">
              Please locate a staff member for help. A staff member will read you a one-time code.
              <div className="tw:font-bold">Enter the 5-digit code below.</div>
            </div>
          </>
        )}
        {isExistingUser && (
          <>
            <div className="fs-22 txt-pri mb-5 text-center tw:font-bold">
              We found an existing account
            </div>
            <div className="text-center mt-3 mb-4 workflow-container-sm">
              We found an account that matches your information. Please locate a staff member for
              help. A staff member will read you a one-time code.
              <div className="tw:font-bold">Enter the 5-digit code below.</div>
            </div>
          </>
        )}
        <div className="my-5">
          {sharedWorkflowData && sharedWorkflowData.workflow_instance && renderForm()}
        </div>
      </div>
    </>
  );
};

export default connect(null, { showAlertWithAction })(withRouter(EnterOTC));
